import { useEffect, useState } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { Mail, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getAuthActionCodeSettings } from '../lib/auth/authActionUrls';
import { Button } from './ui/button';

/** How often to ask Firebase again whether the address has been verified while the banner is up. */
const RECHECK_INTERVAL_MS = 30_000;

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Firebase caches `emailVerified` on the signed-in User object and only refreshes it on
  // `user.reload()` or a fresh sign-in; nothing in the app called reload, so an account that
  // verified from the email link (usually in another tab) kept seeing this banner until it signed
  // out and back in. Track verification here and re-check on mount, whenever the tab regains
  // focus (the moment someone comes back from their inbox), and on a slow interval.
  const [verified, setVerified] = useState<boolean>(() => !!user?.emailVerified);

  useEffect(() => {
    if (!user || user.emailVerified) {
      setVerified(!!user?.emailVerified);
      return;
    }
    let cancelled = false;
    const recheck = async () => {
      try {
        await user.reload();
        if (!cancelled && user.emailVerified) setVerified(true);
      } catch {
        /* offline or token hiccup: keep showing the banner and try again on the next trigger */
      }
    };
    void recheck();
    const onVisible = () => { if (document.visibilityState === 'visible') void recheck(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => void recheck(), RECHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [user]);

  if (!user || user.emailVerified || verified || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    setError(null);
    try {
      await sendEmailVerification(user, getAuthActionCodeSettings('/?emailVerified=1'));
      setSent(true);
    } catch {
      setError('Could not send verification email. Try again in a few minutes.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="border-b border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-100"
      role="status"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
        <div className="flex min-w-0 items-start gap-2 text-sm">
          <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            {sent
              ? 'Verification email sent: check your inbox and spam folder.'
              : 'Please verify your email address to secure your account.'}
            {error ? <span className="mt-1 block text-red-700 dark:text-red-300">{error}</span> : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {!sent && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-amber-300 bg-[var(--surface-card)]/80 text-amber-950 hover:bg-amber-100 dark:border-amber-700 dark:bg-transparent dark:text-amber-100 dark:hover:bg-amber-900/50 min-h-11"
              disabled={sending}
              onClick={() => void handleResend()}
            >
              {sending ? 'Sending…' : 'Resend email'}
            </Button>
          )}
          <button
            type="button"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/50"
            aria-label="Dismiss"
            onClick={() => setDismissed(true)}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
